import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PillarIcon from '../components/PillarIcon';
import { brand, enterprisePillars } from '../config/brand';

export default function EnterprisePage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Enterprise — The Uclipse Standard',
    url: `${brand.siteUrl}/enterprise`,
    description:
      'The Uclipse Standard for enterprise: the Molecular Purity Standard, the Uclipse Bioenzyme flagship, the Precision Lab Group, and Operational Assurance.',
    isPartOf: { '@type': 'WebSite', name: 'Uclipse', url: `${brand.siteUrl}/` },
    hasPart: enterprisePillars.map((p) => ({
      '@type': 'WebPage',
      name: p.name,
      url: `${brand.siteUrl}${p.route}`,
    })),
  };

  return (
    <>
      <Seo
        title="Enterprise — The Uclipse Standard"
        description="Beyond a supplier. The Uclipse Standard for enterprise: ultra-pure chemistry, a flagship bioenzyme platform, concierge engineering, and operational assurance."
        canonicalUrl={`${brand.siteUrl}/enterprise`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(55% 75% at 80% 0%, rgba(14,91,168,0.55) 0%, rgba(2,6,23,0) 60%), radial-gradient(45% 55% at 5% 100%, rgba(123,193,66,0.16) 0%, rgba(2,6,23,0) 60%)',
          }}
        />
        <div className="container-page relative max-w-4xl py-20 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] ring-1 ring-white/20 backdrop-blur">
            For Enterprise
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight md:text-6xl">
            Beyond a supplier.<br />
            <span className="text-brand-300">A standard.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300">
            The most demanding facilities don’t buy chemicals — they invest in certainty. The Uclipse Standard is
            built on four commitments that turn cleaning from a cost line into an operational advantage.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="container-page py-16">
        <div className="grid gap-6 md:grid-cols-2">
          {enterprisePillars.map((p) => (
            <Link
              key={p.id}
              to={p.route}
              className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-card transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-lg"
            >
              <div className="flex items-center gap-4">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
                  <PillarIcon icon={p.icon} className="h-6 w-6" />
                </span>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-brand-600">{p.eyebrow}</div>
                  <h2 className="font-display text-xl font-bold text-slate-900">{p.name}</h2>
                </div>
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">{p.blurb}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 group-hover:gap-2">
                Explore <span aria-hidden>→</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-700 py-16 text-white">
        <div className="container-page text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Build a programme, not a purchase order</h2>
          <p className="mx-auto mt-3 max-w-2xl text-brand-50/90">
            Engage the Precision Lab Group to audit your operation, or request the enterprise technical dossier for
            your sector.
          </p>
          <div className="mt-7 flex justify-center gap-3">
            <Link to="/precision-lab" className="btn bg-white text-brand-700 hover:bg-slate-100">
              Meet the Precision Lab Group
            </Link>
            <Link to="/assurance" className="btn bg-accent-green text-white hover:bg-green-600">
              Request the dossier
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
